// Product image sources that are safe to render.
//
// Product images are remote (Google's image CDN, retailer CDNs). About 170,600 AU
// rows written in 2023 instead point at local files — image "/images/<slug>.webp",
// thumbnails "/images/<slug>-tn1.webp" — that the old SST site served from its S3
// bucket (_assets/images). Those files were deleted (scripts/buywisely-s3-cleanup.sh)
// and never existed in this app's public/, so every such path is a broken image
// (/_next/image answers 400). The rows carry no remote alternative (their thumbnails
// are the same deleted files), so they render the placeholder instead.

export const PLACEHOLDER_IMAGE = '/assets/placeholder-image.webp';

const DELETED_LOCAL_IMAGES = '/images/';

/** True when `src` can actually be served: a remote URL or a shipped local asset. */
export function isUsableProductImage(src: unknown): src is string {
  if (typeof src !== 'string' || !src.trim()) return false;
  if (/^https?:\/\//i.test(src)) return true;
  return src.startsWith('/') && !src.startsWith(DELETED_LOCAL_IMAGES);
}

/** The image to render for a product: its own if usable, else the placeholder. */
export function productImageSrc(src: unknown): string {
  return isUsableProductImage(src) ? src : PLACEHOLDER_IMAGE;
}

/** Usable thumbnails only (drops the deleted /images/ files). */
export function productThumbnails(list: unknown): string[] {
  return Array.isArray(list) ? list.filter(isUsableProductImage) : [];
}

/** Absolute image URL for structured data / Open Graph, or undefined if there is none. */
export function productImageUrl(src: unknown, origin: string): string | undefined {
  if (!isUsableProductImage(src)) return undefined;
  return /^https?:\/\//i.test(src) ? src : new URL(src, origin).toString();
}
